import {
  PaymentService,
  selectOrderCardState,
  orderCardActions,
  ChatModalComponent,
  ChatModalData,
  selectChat,
} from '@aa/angular/core';
import {
  OrderCardResourceTypeMappings,
  OrderCardServiceLinkageResourceTypeMappings,
} from '@aa/nest/resource';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, map, of } from 'rxjs';
import { CustomerAppState } from '../../state/index.reducers';
import { ViewBarComponent } from '../view-bar/view-bar.component';
import * as inflection from 'inflection';
import { MatDialog } from '@angular/material/dialog';

type AdjustedServicesSummary = {
  [
    cardId: number
  ]: (OrderCardServiceLinkageResourceTypeMappings['resourceWithRelationsT'] & {
    deleted?: boolean;
    added?: boolean;
  })[];
};

@Component({
  selector: 'aa-review-cards',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    ViewBarComponent,
  ],
  templateUrl: './review-cards.component.html',
  styleUrl: './review-cards.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewCardsComponent {
  @Input() orderId?: number;
  @Input() hideChat = false;

  @Input()
  hideDraftedChanges = true;

  orderId$: Observable<number | undefined>;
  adjustedServicesSummary$: Observable<AdjustedServicesSummary>;
  cards$: Observable<OrderCardResourceTypeMappings['resourceWithRelationsT'][]>;
  backPath$: Observable<string>;
  unreadCounts: { [cardId: number]: number } = {};

  constructor(
    private readonly store: Store<CustomerAppState>,
    private readonly route: ActivatedRoute,
    private readonly paymentService: PaymentService,
    private readonly dialog: MatDialog,
  ) {
    this.store
      .select((s) => selectChat(s).activeThreads)
      .pipe(takeUntilDestroyed())
      .subscribe((activeThreads) => {
        const counts: { [cardId: number]: number } = {};
        for (const at of activeThreads) {
          const cardId = at.thread.orderCardId;
          if (cardId) {
            counts[cardId] = at.messages.filter(
              (m) => !m.customerProfileId && !m.readAt,
            ).length;
          }
        }
        this.unreadCounts = counts;
      });

    this.cards$ = this.store
      .select((s) => selectOrderCardState(s).items)
      .pipe(takeUntilDestroyed());
    this.adjustedServicesSummary$ = this.cards$.pipe(
      map((cards) => {
        let adjustedServicesSummary: AdjustedServicesSummary = {};

        for (const card of cards) {
          const hasDraftedChanges = !!card.orderCardServiceLinkages?.find(
            (linkage) => linkage.isDraft,
          );
          adjustedServicesSummary[card.id] = [];
          for (const linkage of card.orderCardServiceLinkages ?? []) {
            if (hasDraftedChanges) {
              if (linkage.isDraft) {
                if (
                  !card.orderCardServiceLinkages?.find(
                    (existing) =>
                      existing.offeredServiceId == linkage.offeredServiceId &&
                      !existing.isDraft,
                  )
                ) {
                  adjustedServicesSummary[card.id].push({
                    ...(linkage as any),
                    added: true,
                  });
                } else {
                  adjustedServicesSummary[card.id].push({
                    ...(linkage as any),
                  });
                }
              } else {
                if (
                  !card.orderCardServiceLinkages?.find(
                    (existing) =>
                      existing.offeredServiceId == linkage.offeredServiceId &&
                      existing.isDraft,
                  )
                ) {
                  adjustedServicesSummary[card.id].push({
                    ...(linkage as any),
                    deleted: true,
                  });
                }
              }
            } else {
              adjustedServicesSummary[card.id].push(linkage as any);
            }
          }
          adjustedServicesSummary[card.id] = adjustedServicesSummary[
            card.id
          ].sort((a, b) => a.offeredServiceId - b.offeredServiceId);
        }

        return adjustedServicesSummary;
      }),
    );
    this.orderId$ = this.route.paramMap.pipe(
      map((paramMap) => parseInt(paramMap.get('orderId')!)),
      takeUntilDestroyed(),
    );
    this.backPath$ = this.orderId$.pipe(
      map((draftId) => `/draft-editor/${draftId}`),
    );

    this.orderId$.subscribe((draftId) => {
      if (draftId) {
        this.store.dispatch(
          orderCardActions.loadItems({
            query: {
              where: {
                orderId: draftId,
              },
              include: {
                orderCardAttributes: {
                  include: {
                    orderCardAttributeType: true,
                  },
                },
                orderCardServiceLinkages: {
                  include: {
                    offeredService: true,
                  },
                },
              },
            },
          }),
        );
      }
    });
  }

  ngAfterViewInit() {
    if (this.orderId) this.orderId$ = of(this.orderId);
  }

  openChat(orderCardId: number) {
    this.dialog.open(ChatModalComponent, {
      data: {
        orderCardId,
      } as ChatModalData,
      height: 'fit-content',
      maxHeight: 'fit-content',
      minWidth: '50vw',
      maxWidth: 'calc(100dvw - 5rem)',
      width: '50rem',
    });
  }

  displayCardAttributeValue(fieldName: string | undefined, value: string) {
    let attributeValue = value;
    if (
      fieldName == 'pokemonType' &&
      !!attributeValue &&
      attributeValue.includes('[')
    ) {
      attributeValue = JSON.parse(value).join(', ');
    }
    if (fieldName == 'cardGrade') {
      return attributeValue;
    }
    return inflection.titleize(`${attributeValue ?? ''}`.replaceAll('-', ' '));
  }

  isString(value: string) {
    return typeof value == 'string';
  }
}
